import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import EditTripModal from './EditTripModal';
import AddBookingModal from './AddBookingModal';
import AssignAssociateModal from './AssignAssociateModal';
import EditBookingModal from './EditBookingModal';
import BookingDetailsModal from './BookingDetailsModal';
import ClientItinerary from './ClientItinerary';
import API_BASE_URL from '../config';
import './DetailComponents.css';
import { useNavigate } from 'react-router-dom';

function TripDetails({ tripId, isDashboard = false, onTripUpdate }) {
  const [trip, setTrip] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTrip, setEditingTrip] = useState(false);
  const [addingBooking, setAddingBooking] = useState(false);
  const [assigningAssociate, setAssigningAssociate] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [viewingBookingId, setViewingBookingId] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [previewAsClient, setPreviewAsClient] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch itinerary and client data on mount
  useEffect(() => {
    const fetchTripData = async () => {
      try {
        console.log('🔍 Fetching itinerary with ID:', tripId);
        const tripResponse = await axios.get(`${API_BASE_URL}/api/trips/${tripId}`);
        console.log('📡 Itinerary response:', tripResponse.data);
        const tripData = tripResponse.data.data || tripResponse.data;
        console.log('📊 Itinerary data:', tripData);
        if (!tripData || !tripData.clientId) {
          console.log('❌ No itinerary data or clientId found');
          setLoading(false);
          return;
        }
        setTrip(tripData);

        console.log('👤 Fetching client with ID:', tripData.clientId);
        const clientResponse = await axios.get(`${API_BASE_URL}/api/clients/${tripData.clientId}`);
        console.log('📡 Client response:', clientResponse.data);
        const clientData = clientResponse.data.data || clientResponse.data;
        console.log('📊 Client data:', clientData);
        if (!clientData) {
          console.log('❌ No client data found');
          setLoading(false);
          return;
        }
        setClient(clientData);

        const associates = Array.isArray(tripData.travelAssociateIds)
          ? tripData.travelAssociateIds
              .map(id => clientData.travelAssociates?.find(assoc => assoc.id === id) || null)
              .filter(associate => associate)
          : [];
        setTrip(prevTrip => ({ ...prevTrip, travelAssociates: associates }));
      } catch (error) {
        setTrip(null);
        setClient(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTripData();
  }, [tripId]);

  const handleDeleteTrip = () => {
    if (window.confirm('Are you sure you want to delete this itinerary?')) {
      axios.delete(`${API_BASE_URL}/api/trips/${tripId}`)
        .then(() => navigate('/'))
        .catch(() => {});
    }
  };

  const handleSaveTrip = (updatedTrip) => {
    axios.put(`${API_BASE_URL}/api/trips/${tripId}`, updatedTrip)
      .then(response => {
        const updatedTripData = response.data.data;
        setTrip(updatedTripData);
        setEditingTrip(false);
        
        // Notify parent component about the update
        if (onTripUpdate) {
          onTripUpdate(updatedTripData);
        }
      })
      .catch(() => {});
  };

  const handleToggleShowItinerary = () => {
    const updatedTrip = { ...trip, showItinerary: !trip.showItinerary };
    axios.put(`${API_BASE_URL}/api/trips/${tripId}`, updatedTrip)
      .then(response => {
        const updatedTripData = response.data.data;
        const associates = Array.isArray(updatedTripData.travelAssociateIds)
          ? updatedTripData.travelAssociateIds
              .map(id => client.travelAssociates?.find(assoc => assoc.id === id) || null)
              .filter(associate => associate)
          : [];
        const finalTrip = { ...updatedTripData, travelAssociates: associates };
        setTrip(finalTrip);
        
        // Notify parent component about the update
        if (onTripUpdate) {
          onTripUpdate(finalTrip);
        }
      })
      .catch(() => {});
  };

  const handleSaveBooking = (bookingData) => {
    axios.post(`${API_BASE_URL}/api/trips/${tripId}/bookings`, bookingData)
      .then(response => {
        const newBooking = response.data.data;
        const updatedBookings = [...trip.bookings, newBooking].sort((a, b) => 
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        );
        setTrip({ ...trip, bookingCount: trip.bookingCount + 1, bookings: updatedBookings });
        setAddingBooking(false);
      })
      .catch(() => {});
  };

  const handleEditBooking = (booking, index) => {
    setEditingBookingId(index);
  };

  const handleSaveEditedBooking = (updatedBooking) => {
    axios.put(`${API_BASE_URL}/api/trips/${tripId}/bookings/${editingBookingId}`, updatedBooking)
      .then(response => {
        const updatedBookings = [...trip.bookings];
        updatedBookings[editingBookingId] = response.data.data;
        const sortedBookings = updatedBookings.sort((a, b) => 
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        );
        setTrip({ ...trip, bookings: sortedBookings });
        setEditingBookingId(null);
      })
      .catch(() => {});
  };

  const handleViewBookingDetails = (bookingIndex) => {
    setViewingBookingId(bookingIndex);
  };

  const handleDeleteBooking = (bookingIndex) => {
    if (bookingIndex === null || bookingIndex === undefined) return;
    if (window.confirm('Are you sure you want to delete this booking?')) {
      axios.delete(`${API_BASE_URL}/api/trips/${tripId}/bookings/${bookingIndex}`)
        .then(() => {
          const updatedBookings = trip.bookings.filter((_, index) => index !== bookingIndex).sort((a, b) => 
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
          );
          setTrip({ ...trip, bookings: updatedBookings, bookingCount: updatedBookings.length });
        })
        .catch(() => {});
    }
  };

  const handleSaveAssociate = (associateId) => {
    const updatedAssociateIds = Array.isArray(trip.travelAssociateIds) 
      ? [...trip.travelAssociateIds, associateId] 
      : [associateId];
    axios.put(`${API_BASE_URL}/api/trips/${tripId}`, { ...trip, travelAssociateIds: updatedAssociateIds })
      .then(response => {
        const updatedTrip = response.data.data;
        if (!updatedTrip || !Array.isArray(updatedTrip.travelAssociateIds)) return;
        const associates = updatedTrip.travelAssociateIds
          .map(id => client.travelAssociates?.find(assoc => assoc.id === id) || null)
          .filter(associate => associate);
        setTrip({ ...updatedTrip, travelAssociates: associates });
        setAssigningAssociate(false);
      })
      .catch(() => setAssigningAssociate(false));
  };

  const handleRemoveAssociate = (associateId) => {
    const updatedAssociateIds = trip.travelAssociateIds.filter(id => id !== associateId);
    axios.put(`${API_BASE_URL}/api/trips/${tripId}`, { ...trip, travelAssociateIds: updatedAssociateIds })
      .then(response => {
        const updatedTrip = response.data.data;
        if (!updatedTrip || !Array.isArray(updatedTrip.travelAssociateIds)) return;
        const associates = updatedTrip.travelAssociateIds
          .map(id => client.travelAssociates?.find(assoc => assoc.id === id) || null)
          .filter(associate => associate);
        setTrip({ ...updatedTrip, travelAssociates: associates });
      })
      .catch(() => {});
  };

  const handleBack = () => navigate('/');

  const handlePreviewAsClient = () => {
    setPreviewAsClient(true);
    setShowMenu(false);
  };

  const handleExitPreview = () => {
    setPreviewAsClient(false);
  };

  const toggleMenu = () => setShowMenu(!showMenu);

  const formatUTCDate = (date) => {
    const d = new Date(date);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  };

  const formatUTCDateTime = (date) => {
    const d = new Date(date);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  };

  // Determine icon class based on booking type and details
  const getIconClass = (type, details = {}) => {
    if (type === 'Transport' && details.transportType) {
      const transportIcons = {
        'Taxi': 'icon-taxi',
        'Limousine': 'icon-limousine',
        'Shuttle': 'icon-shuttle',
        'Bus': 'icon-bus',
        'Van': 'icon-van'
      };
      return transportIcons[details.transportType] || 'icon-transport-default';
    }
    if (type === 'Event' && details.eventType) {
      const eventIcons = {
        'Concert': 'icon-event-concert',
        'Festival': 'icon-event-festival',
        'Wedding': 'icon-wedding',
        'Conference': 'icon-event-conference',
        'Other': 'icon-event-other'
      };
      return eventIcons[details.eventType] || 'icon-event-default';
    }

        if (type === 'Day Tours & Activities') {
    const activityIcons = {
      'Hike': 'icon-hike',
      'Adventure': 'icon-adventure',
      'Cultural': 'icon-cultural',
      'Cooking Class': 'icon-cooking',
      'Wildlife': 'icon-wildlife',
      'Other': 'icon-tour-default'
    };
    return activityIcons[details.activityType] || 'icon-tour';
  }
  
    const iconClasses = {
      'Hotels': 'icon-hotel',
      'AirBnb': 'icon-airbnb',
      'Flights': 'icon-flight',
      'Cruise': 'icon-cruise',
      'DMCs': 'icon-dmc',
      'Homes/Villas': 'icon-home',
      'Insurance': 'icon-insurance',
      'Packages': 'icon-package',
      'Transportation': 'icon-transport',
      'Multiday Tours': 'icon-tour-multi',
      'Other': 'icon-other',
      'Restaurant': 'icon-restaurant',
      'Car Rental': 'icon-carrental',
      'Transport': 'icon-transport-default'
    };
    return iconClasses[type] || 'icon-default';
  };

  const sortedBookings = trip?.bookings
    ? [...trip.bookings].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    : [];

  if (loading) return <div>Loading itinerary details...</div>;
  if (!trip) return <div>Itinerary not found</div>;

  return (
    <div className={isDashboard ? 'dashboard-integrated' : ''}>
      <div className={`App trip-details ${isDashboard ? 'dashboard-mode' : ''}`}>
        {!isDashboard && <h1>Itinerary Details</h1>}
        {!isDashboard && (
          <div className="header-section">
            <button className="back-button" onClick={handleBack}>&lt;</button>
          </div>
        )}
        <div className={`info-box trip-details-box ${isDashboard ? 'dashboard-style' : ''}`} style={{ maxWidth: isDashboard ? '100%' : '700px !important', margin: isDashboard ? '0 auto' : '20px auto' }}>
          <div className={`details-actions ${isDashboard ? 'dashboard-actions' : ''}`} ref={menuRef}>
            {isDashboard ? (
              <div className="dashboard-action-buttons">
                <button className="dashboard-action-btn" onClick={() => setEditingTrip(true)}>
                  Edit Itinerary
                </button>
                <button className="dashboard-action-btn" onClick={() => setAddingBooking(true)}>
                  Add Booking
                </button>
                <button className="dashboard-action-btn" onClick={() => setAssigningAssociate(true)}>
                  Assign Travel Associate
                </button>
                <button className="dashboard-action-btn" onClick={handlePreviewAsClient}>
                  Preview as Client
                </button>
                <button className="dashboard-action-btn danger" onClick={handleDeleteTrip}>
                  Delete Itinerary
                </button>
              </div>
            ) : (
              <>
                <button className="actions-menu-button" onClick={toggleMenu}>⋮</button>
                {showMenu && (
                  <div className="actions-menu">
                    <div className="actions-menu-item" onClick={() => { setEditingTrip(true); setShowMenu(false); }}>Edit</div>
                    <div className="actions-menu-item" onClick={() => { handleDeleteTrip(); setShowMenu(false); }}>Delete</div>
                    <div className="actions-menu-item" onClick={() => { setAddingBooking(true); setShowMenu(false); }}>Add Booking</div>
                    <div className="actions-menu-item" onClick={() => { setAssigningAssociate(true); setShowMenu(false); }}>Assign Travel Associate</div>
                    <div className="actions-menu-item" onClick={handlePreviewAsClient}>Preview as Client</div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>{trip.title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="label">Publish:</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={trip.showItinerary || false}
                  onChange={handleToggleShowItinerary}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        <div className="details-box">
          <div className="details-grid">
            {/* Single Column */}
            <div className="details-column">
              {/* Client and Status on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Client:</span> {trip.traveler}
                </div>
                <div className="details-item">
                  <span className="label">Status:</span>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    backgroundColor: trip.status === 'confirmed' ? '#28a745' :
                                     trip.status === 'planning' ? '#ffc107' : '#6c757d',
                    color: trip.status === 'planning' ? '#000' : '#fff',
                    marginLeft: '5px'
                  }}>
                    {trip.status}
                  </span>
                </div>
              </div>

              {/* Start Date and End Date on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Start Date:</span> {formatUTCDate(trip.startDate)}
                </div>
                <div className="details-item">
                  <span className="label">End Date:</span> {formatUTCDate(trip.endDate)}
                </div>
              </div>

              {/* Duration and Bookings on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Duration:</span> {trip.durationInDays} days
                </div>
                <div className="details-item">
                  <span className="label">Bookings:</span> {trip.bookingCount}
                </div>
              </div>

              {/* Travel Associates */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Travel Associates:</span>
                  {trip.travelAssociates?.length > 0 ? trip.travelAssociates.length : 'None'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Travel Associates Section */}
        {trip.travelAssociates?.length > 0 && (
          <div className="details-section travel-associates">
            <div className="details-section-title">
              <h3>Travel Associates</h3>
            </div>
            <div>
              {trip.travelAssociates.map((associate, index) => (
                associate && (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #e9ecef'
                  }}>
                    <button 
                      className="delete-button" 
                      onClick={() => handleRemoveAssociate(associate.id)}
                      style={{
                        marginRight: '10px',
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      X
                    </button>
                    <span>
                      <strong>{associate.name}</strong>
                      {associate.relationship && ` (${associate.relationship})`}
                    </span>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {editingTrip && (
          <EditTripModal 
            trip={trip} 
            onSave={handleSaveTrip} 
            onClose={() => setEditingTrip(false)} 
          />
        )}

        <div className="details-section bookings-section">
          <div className="details-section-title">
            <h3>Bookings</h3>
          </div>
          {sortedBookings.length === 0 ? (
            <p>No bookings yet</p>
          ) : (
            <table className="details-table bookings-table" style={{padding: '10px 0 10px 0'}}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedBookings.map((booking, index) => (
                  <tr key={index}>
                    <td>
                      <span className={`${getIconClass(booking.type, booking.details)} icon-entry`}></span>
                    </td>
                    <td>
                      {formatUTCDateTime(booking.startDate)}<br />
                      {formatUTCDateTime(booking.endDate)}
                    </td>
                    <td>{booking.bookingName}</td>
                    <td>
                      <button className="edit-button" onClick={() => handleEditBooking(booking, index)}>✎</button>
                      <button className="delete-button" onClick={() => handleDeleteBooking(index)}>X</button>
                      <button className="details-button" onClick={() => handleViewBookingDetails(index)}>Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {addingBooking && (
          <AddBookingModal 
            tripId={tripId}
            tripStartDate={trip.startDate}
            tripEndDate={trip.endDate}
            onSave={handleSaveBooking} 
            onClose={() => setAddingBooking(false)} 
          />
        )}
        {editingBookingId !== null && (
          <EditBookingModal 
            booking={trip.bookings[editingBookingId]}
            onSave={handleSaveEditedBooking}
            onClose={() => setEditingBookingId(null)}
          />
        )}
        {viewingBookingId !== null && (
          <BookingDetailsModal 
            booking={trip.bookings[viewingBookingId]}
            onClose={() => setViewingBookingId(null)}
          />
        )}
        {assigningAssociate && client && (
          <AssignAssociateModal 
            associates={client.travelAssociates || []}
            assignedAssociateIds={trip.travelAssociateIds || []}
            onSave={handleSaveAssociate} 
            onClose={() => setAssigningAssociate(false)} 
          />
        )}

        {/* Preview as Client */}
        {previewAsClient && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'white', 
            zIndex: 9999,
            overflow: 'auto'
          }}>
            <div style={{ 
              padding: '20px',
              borderBottom: '1px solid #ddd',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: '#666' }}>Client Preview - {trip.title}</h2>
              <button 
                onClick={handleExitPreview}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Exit Preview
              </button>
            </div>
            <ClientItinerary 
              tripData={trip} 
              clientNameData={trip.traveler} 
            />
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

export default TripDetails;